import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation } from 'router-v6'
import { REGIONS } from 'v2/configs/constants'
import { getActiveRegion } from 'v2/shared/services/create-service'
import {
  ElevenLabsModels,
  getEventCallbackDefaults,
} from '../config/agent-settings.validation.config'
import type { AgentSettingsFormValues } from '../config/base.config'
import { userIdleConfig } from '../constants'
import { useGetLanguages, useGetProviders } from '../knowledge-base/queries'
import { useFlowStore } from '../store'
import type { FlowData } from '../types'
import {
  getVoiceAiConfig,
  processLanguageSelection,
  SUPPORTED_LANGUAGES,
} from '../utils/lang-utils'
import { cleanConversationGoalsForExport } from '../utils/node-cleanup'
import { validateVoiceAiConfig } from '../utils/stt-tts-validator'

interface UseAgentSettingsProps {
  flowData?: FlowData
}

export function useAgentSettings({ flowData }: UseAgentSettingsProps) {
  // Session region in embed (localStorage auth is empty there), console auth otherwise.
  const activeRegion = getActiveRegion()
  const region = activeRegion || REGIONS.US
  const isINRegion = activeRegion === REGIONS.IN
  const location = useLocation()
  const importedAgentSettingsFromState = location.state?.importedAgentSettings as
    | AgentSettingsFormValues
    | undefined
  const importedAgentSettingsRef = useRef<AgentSettingsFormValues | undefined>(undefined)
  if (importedAgentSettingsFromState) {
    importedAgentSettingsRef.current = importedAgentSettingsFromState
  }
  const importedAgentSettings = importedAgentSettingsFromState ?? importedAgentSettingsRef.current

  // Get languages using the new hook with SUPPORTED_LANGUAGES filter
  // biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
  const { data: languagesData } = useGetLanguages((data: any) => ({
    ...data,
    data: {
      ...data.data,
      // biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
      languages: data.data.languages.filter((lang: any) => SUPPORTED_LANGUAGES.includes(lang.code)),
    },
  }))
  const languages = languagesData?.data?.languages || []

  // Check if languages exist in the flow data, otherwise use default languages based on region
  const getDefaultLanguages = (): string => {
    if (flowData?.phlo?.voice_ai_config?.language) {
      // Language is now always a string (comma-separated)
      // Process existing language selection to handle 'en' replacement
      const existingLanguages = flowData.phlo.voice_ai_config.language.split(',').filter(Boolean)
      const processedLanguages = processLanguageSelection(existingLanguages, region)
      return processedLanguages.join(',')
    }

    if (isINRegion) {
      // biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
      const hasEnIN = languages?.some((language: any) => language.code === 'en-IN')
      if (hasEnIN) return 'en-IN'
      // If en-IN not available, use first language from the list or default to en-IN
      const firstLang = languages?.[0]?.code || 'en-IN'
      return firstLang
    }
    return 'en-US' // Default to region-specific English variant
  }

  const selectedLanguages = getDefaultLanguages()
  const { data: providers } = useGetProviders()
  const setAgentSettings = useFlowStore((state) => state.setAgentSettings)
  const setSystemPrompt = useFlowStore((state) => state.setSystemPrompt)
  const setSttGuidance = useFlowStore((state) => state.setSttGuidance)

  // Track which flow ID has been initialized to prevent re-initialization on refetch
  const initializedFlowId = useRef<string | null>(null)
  const initializedWithProviders = useRef(false)
  // Which flow the unsaved-changes baseline (initialAgentSettings) was last taken for.
  // The hook runs once before the flow query resolves (flowData undefined) and seeds
  // defaults; when the real flow arrives the baseline has to be taken again, otherwise
  // every loaded flow compares against defaults and reads as "unsaved" (a tagged flow
  // would show its tag as an unsaved change and hold Copy cURL).
  const initialSnapshotFlowId = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    // Only initialize once per flow - skip if same flow refetched (e.g. after save)
    // Allow re-init if providers weren't available on first run
    if (
      initializedFlowId.current === flowData?.phlo?.id &&
      !importedAgentSettings &&
      initializedWithProviders.current
    )
      return
    initializedFlowId.current = flowData?.phlo?.id || null
    initializedWithProviders.current = !!providers?.data

    queueMicrotask(() => {
      // The baseline for unsaved-changes detection: taken on the first seed and again
      // when the seed is for a different flow than the last baseline (the undefined ->
      // loaded transition on a cold load). A refetch of the same flow keeps the baseline
      // the save path wrote.
      const takeInitialSnapshot = (settingsPayload: AgentSettingsFormValues) => {
        const snapshotFlowId = flowData?.phlo?.id ?? null
        if (
          !useFlowStore.getState().initialAgentSettings ||
          initialSnapshotFlowId.current !== snapshotFlowId
        ) {
          useFlowStore.getState().setInitialAgentSettings(settingsPayload)
          initialSnapshotFlowId.current = snapshotFlowId
        }
      }

      // Get STT/TTS configuration based on selected languages and region
      const selectedLanguagesArray = selectedLanguages.split(',').filter(Boolean)
      const sttTtsConfig = getVoiceAiConfig(selectedLanguagesArray, region, providers?.data)

      const defaults = getEventCallbackDefaults()
      const eventCallbacks = {
        HANGUP: {
          ...defaults.HANGUP,
          ...flowData?.phlo?.event_callbacks?.HANGUP,
        },
        RECORDING: {
          ...defaults.RECORDING,
          ...flowData?.phlo?.event_callbacks?.RECORDING,
        },
        ERROR: {
          ...defaults.ERROR,
          ...flowData?.phlo?.event_callbacks?.ERROR,
        },
        SMS_STATUS: {
          ...defaults.SMS_STATUS,
          ...flowData?.phlo?.event_callbacks?.SMS_STATUS,
        },
        WHATSAPP_STATUS: {
          ...defaults.WHATSAPP_STATUS,
          ...flowData?.phlo?.event_callbacks?.WHATSAPP_STATUS,
        },
        CONVERSATION_CLOSURE: {
          ...defaults.CONVERSATION_CLOSURE,
          ...flowData?.phlo?.event_callbacks?.CONVERSATION_CLOSURE,
        },
      }
      if (importedAgentSettings) {
        // Process imported settings with the same region-specific language processing
        const importedLanguages =
          importedAgentSettings.voice_ai_config?.language?.split(',').filter(Boolean) || []
        const processedLanguages = processLanguageSelection(importedLanguages, region)
        const processedLanguageString = processedLanguages.join(',')

        // Get STT/TTS configuration for the processed languages
        const sttTtsConfig = getVoiceAiConfig(processedLanguages, region, providers?.data)

        // Create processed voice AI config
        const processedVoiceAiConfig = {
          ...importedAgentSettings.voice_ai_config,
          language: processedLanguageString,
          text_to_speech: {
            ...importedAgentSettings.voice_ai_config?.text_to_speech,
            provider:
              importedAgentSettings.voice_ai_config?.text_to_speech?.provider ||
              sttTtsConfig.ttsProvider,
            model:
              importedAgentSettings.voice_ai_config?.text_to_speech?.model ||
              sttTtsConfig.ttsModel ||
              ElevenLabsModels.TurboV2_5,
            voice:
              importedAgentSettings.voice_ai_config?.text_to_speech?.voice ||
              sttTtsConfig.defaultVoice,
            voice_label:
              importedAgentSettings.voice_ai_config?.text_to_speech?.voice_label ||
              sttTtsConfig.defaultVoiceLabel,
          },
          speech_to_text: {
            provider:
              importedAgentSettings.voice_ai_config?.speech_to_text?.provider ||
              sttTtsConfig.sttProvider,
            model:
              importedAgentSettings.voice_ai_config?.speech_to_text?.model || sttTtsConfig.sttModel,
            keyterms: importedAgentSettings?.voice_ai_config?.speech_to_text?.keyterms || [],
          },
          user_idle_config: {
            ...(importedAgentSettings.voice_ai_config?.user_idle_config || userIdleConfig),
            hold_timeout:
              importedAgentSettings.voice_ai_config?.user_idle_config?.hold_timeout ||
              userIdleConfig.hold_timeout,
          },
          allow_greeting_interruption:
            importedAgentSettings.voice_ai_config?.allow_greeting_interruption ?? true,
          background_audio: {
            type: importedAgentSettings.voice_ai_config?.background_audio?.type || 'none',
            volume: importedAgentSettings.voice_ai_config?.background_audio?.volume ?? 1,
            thinking_sound_enabled:
              importedAgentSettings.voice_ai_config?.background_audio?.thinking_sound_enabled ??
              true,
            thinking_sound_volume:
              importedAgentSettings.voice_ai_config?.background_audio?.thinking_sound_volume ?? 0.7,
          },
        }

        // Validate the processed voice AI config
        const validatedVoiceAiConfig = validateVoiceAiConfig(
          processedVoiceAiConfig,
          providers?.data,
        )

        const settingsPayload = {
          schedule: importedAgentSettings.schedule || null,
          knowledge_base_ids: importedAgentSettings.knowledge_base_ids || [],
          voice_ai_config: validatedVoiceAiConfig,
          event_callbacks: importedAgentSettings.event_callbacks || eventCallbacks,
          conversation_goals: cleanConversationGoalsForExport(
            importedAgentSettings.conversation_goals,
          ),
          // Seed the subaccount tag from the SERVER value so the staged copy equals
          // the loaded one until the user picks something: the save only sends the
          // tag when the two differ (sub-auth-id-for-save.ts), and the Settings-tab
          // dropdown shows it from here. Do NOT "simplify" this to null.
          sub_auth_id: flowData?.phlo?.sub_auth_id ?? null,
        }
        setAgentSettings(settingsPayload)
        takeInitialSnapshot(settingsPayload)
      } else {
        // Create the voice AI config with backend data
        const voiceAiConfig = {
          language: selectedLanguages,
          text_to_speech: {
            provider:
              flowData?.phlo?.voice_ai_config?.text_to_speech?.provider || sttTtsConfig.ttsProvider,
            model:
              flowData?.phlo?.voice_ai_config?.text_to_speech?.model ||
              sttTtsConfig.ttsModel ||
              ElevenLabsModels.TurboV2_5,
            voice:
              flowData?.phlo?.voice_ai_config?.text_to_speech?.voice || sttTtsConfig.defaultVoice,
            voice_label:
              flowData?.phlo?.voice_ai_config?.text_to_speech?.voice_label ||
              sttTtsConfig.defaultVoiceLabel,
            voice_speed: flowData?.phlo?.voice_ai_config?.text_to_speech?.voice_speed,
            pronunciation: flowData?.phlo?.voice_ai_config?.text_to_speech?.pronunciation || [],
          },
          speech_to_text: {
            provider:
              flowData?.phlo?.voice_ai_config?.speech_to_text?.provider || sttTtsConfig.sttProvider,
            model: flowData?.phlo?.voice_ai_config?.speech_to_text?.model || sttTtsConfig.sttModel,
            keyterms: flowData?.phlo?.voice_ai_config?.speech_to_text?.keyterms || [],
          },
          user_idle_config: {
            ...(flowData?.phlo?.voice_ai_config?.user_idle_config || userIdleConfig),
            hold_timeout:
              flowData?.phlo?.voice_ai_config?.user_idle_config?.hold_timeout ||
              userIdleConfig.hold_timeout,
          },
          allow_greeting_interruption:
            flowData?.phlo?.voice_ai_config?.allow_greeting_interruption ?? true,
          background_audio: {
            type: flowData?.phlo?.voice_ai_config?.background_audio?.type || 'none',
            volume: flowData?.phlo?.voice_ai_config?.background_audio?.volume ?? 1,
            thinking_sound_enabled:
              flowData?.phlo?.voice_ai_config?.background_audio?.thinking_sound_enabled ?? true,
            thinking_sound_volume:
              flowData?.phlo?.voice_ai_config?.background_audio?.thinking_sound_volume ?? 0.7,
          },
        }

        // Validate and process the voice AI config to ensure voice matches processed language
        const validatedVoiceAiConfig = validateVoiceAiConfig(voiceAiConfig, providers?.data)

        const settingsPayload = {
          schedule: flowData?.phlo?.schedule || null,
          knowledge_base_ids: flowData?.phlo?.knowledge_base_ids || [],
          voice_ai_config: validatedVoiceAiConfig,
          event_callbacks: eventCallbacks,
          conversation_goals: flowData?.phlo?.conversation_goals || [],
          // Seed the subaccount tag from the SERVER value so the staged copy equals
          // the loaded one until the user picks something: the save only sends the
          // tag when the two differ (sub-auth-id-for-save.ts), and the Settings-tab
          // dropdown shows it from here. Do NOT "simplify" this to null.
          sub_auth_id: flowData?.phlo?.sub_auth_id ?? null,
        }
        setAgentSettings(settingsPayload)
        takeInitialSnapshot(settingsPayload)
      }
    })
  }, [
    flowData?.phlo?.id,
    selectedLanguages,
    region,
    importedAgentSettings,
    providers,
    flowData?.phlo?.conversation_goals,
    setAgentSettings,
  ])

  // Update system prompt and STT guidance in store when flow data changes
  useLayoutEffect(() => {
    queueMicrotask(() => {
      if (!flowData) return

      const { global_meta: globalMeta } = flowData
      setSystemPrompt(globalMeta?.system_prompt ?? null, false)
      setSttGuidance(globalMeta?.stt_guidance ?? null)
      useFlowStore.getState().setAgentName(globalMeta?.agent_name ?? null)
      useFlowStore.getState().setAgentPersona(globalMeta?.agent_persona ?? null)
      useFlowStore.getState().setIsGlobalMetaLoaded(true)
    })
  }, [flowData?.global_meta, setSystemPrompt, setSttGuidance])

  return { languages, providers }
}
